import { OrderStatus } from '@prisma/client';

import { MAX_ORDER_BOARD_WINDOW, ORDER_BOARD_PAGE_SIZE } from './order.type';

/**
 * Janela carregada de cada aba do painel (D50/D51), como parâmetro de `GET /orders`.
 *
 * `limitByStatus=PENDING:40,DELIVERED:20` — um parâmetro escalar, e não `limitByStatus[PENDING]=40`,
 * porque o parser de query do Express varia entre versões (`extended` monta objeto a partir dos
 * colchetes, `simple` entrega a chave literal `limitByStatus[PENDING]`) e o `ValidationPipe` roda com
 * `forbidNonWhitelisted`: no modo `simple` a chave literal viraria **400** em toda requisição do
 * painel. Um escalar validado por regex no DTO não depende de como o servidor foi configurado.
 *
 * Serializar e parsear vivem no MESMO arquivo, compartilhado pelo backend e pelo frontend: o poll
 * pede a janela que está na tela (D51), e duas implementações do formato são duas verdades sobre
 * quantos pedidos o operador está vendo.
 */

/** Forma aceita: `STATUS:janela`, separado por vírgula. Só valida a FORMA — o valor é resolvido no parse. */
export const LIMIT_BY_STATUS_PATTERN = /^[A-Z_]{1,24}:\d{1,4}(,[A-Z_]{1,24}:\d{1,4})*$/;

/** Comprimento máximo do parâmetro, com folga para as cinco abas no teto de janela. */
export const MAX_LIMIT_BY_STATUS_LENGTH = 120;

/**
 * Janela por status para o `where`/`take` do servidor, com **todas** as abas preenchidas.
 *
 * Ausente, ilegível ou fora do teto cai no default: o painel precisa continuar carregando. Nome de
 * status desconhecido é ignorado — só chega aí por URL montada à mão (o cliente monta a partir do
 * enum, com `Record<OrderStatus, …>` exaustivo), e o efeito é a janela default, nunca escopo errado.
 *
 * `defaultWindow` é como o servidor honra o `limit` do `QueryBaseDto` para quem consulta uma aba só
 * (a integração da F9) sem um segundo mecanismo de paginação. O painel não manda `limit`, então
 * cliente e servidor concordam no default de `serializeLimitByStatus`.
 */
export function parseLimitByStatus(
  value: string | undefined | null,
  defaultWindow: number = ORDER_BOARD_PAGE_SIZE,
): Record<OrderStatus, number> {
  const fallback = clampOrderBoardWindow(defaultWindow);
  const windowByStatus = Object.values(OrderStatus).reduce(
    (accumulator, status) => ({ ...accumulator, [status]: fallback }),
    {} as Record<OrderStatus, number>,
  );

  if (!value) {
    return windowByStatus;
  }

  for (const entry of value.split(',')) {
    const [name, size] = entry.split(':');
    const status = Object.values(OrderStatus).find((candidate) => candidate === name?.trim());
    if (!status) {
      continue;
    }
    windowByStatus[status] = clampOrderBoardWindow(Number(size));
  }

  return windowByStatus;
}

/**
 * Só as abas fora do default entram no parâmetro, e o resultado é `''` quando todas estão no
 * default: a URL do poll tem de ser **estável** enquanto o operador não rola nada, senão cada
 * reavaliação do recurso vira uma requisição diferente.
 */
export function serializeLimitByStatus(windowByStatus: Partial<Record<OrderStatus, number>>): string {
  return Object.values(OrderStatus)
    .filter((status) => {
      const size = windowByStatus[status];
      return typeof size === 'number' && Number.isFinite(size) && size !== ORDER_BOARD_PAGE_SIZE;
    })
    .map((status) => `${status}:${clampOrderBoardWindow(windowByStatus[status] as number)}`)
    .join(',');
}

/** Uma aba nunca carrega menos que uma página nem mais que o teto — o poll revalida a janela inteira. */
export function clampOrderBoardWindow(size: number): number {
  if (!Number.isFinite(size)) {
    return ORDER_BOARD_PAGE_SIZE;
  }
  return Math.min(Math.max(Math.trunc(size), ORDER_BOARD_PAGE_SIZE), MAX_ORDER_BOARD_WINDOW);
}
